import { useEffect, useState, type ReactNode } from 'react';
import { motion } from 'motion/react';
import { Check, Hourglass } from 'lucide-react';
import { Avatar } from './Avatar';
import { usePartyUi } from './context';

/**
 * Building blocks for games' audience-screen (TV) layouts: sized in viewport units so the same screen
 * reads from across a room at 720p and at 1080p. The platform's TV frame already shows the game name,
 * phase, timer, join code and scoreboard, so these only lay out the game's own content.
 */

/** `vw` viewport-width percent in px, clamped: for sizes that must be numbers (avatars). */
export function useVw(vw: number, min: number, max: number): number {
  const [w, setW] = useState(() => (typeof window === 'undefined' ? 1280 : window.innerWidth));
  useEffect(() => {
    const on = () => setW(window.innerWidth);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return Math.round(Math.max(min, Math.min(max, (w * vw) / 100)));
}

/** A two-column TV scene: the main story on the left, art and who's-playing on the right. */
export function TvScene({ children, side, className = '' }: { children: ReactNode; side?: ReactNode; className?: string }) {
  return (
    <div className={`flex-1 min-h-0 flex gap-[2.2vw] px-[2.2vw] py-[1.8vw] ${className}`} data-testid="tv-scene">
      <div className="flex-1 min-w-0 flex flex-col justify-center gap-[1.4vw]">{children}</div>
      {side && <div className="w-[30%] max-w-[560px] shrink-0 flex flex-col justify-center items-center gap-[1.4vw]">{side}</div>}
    </div>
  );
}

/** Big kicker / title / subline. */
export function TvTitle({ kicker, title, sub, center }: { kicker?: ReactNode; title: ReactNode; sub?: ReactNode; center?: boolean }) {
  return (
    <div className={`flex flex-col gap-[0.6vw] ${center ? 'items-center text-center' : ''}`}>
      {kicker && <span className="chip w-fit uppercase tracking-widest font-black text-[clamp(13px,1.1vw,22px)] text-[var(--accent-2)]">{kicker}</span>}
      <motion.h2
        initial={{ y: 14, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="font-display font-black leading-[1.02] text-balance text-[clamp(30px,3.6vw,76px)]"
        style={{ textShadow: '0 4px 0 color-mix(in oklab, var(--accent) 55%, black)' }}
      >
        {title}
      </motion.h2>
      {sub && <p className="text-paper/85 text-balance text-[clamp(17px,1.6vw,32px)] leading-snug">{sub}</p>}
    </div>
  );
}

/** A clay art piece, gently bobbing. */
export function TvArt({ src, alt = '', className = '', still }: { src: string; alt?: string; className?: string; still?: boolean }) {
  const { reducedMotion } = usePartyUi();
  return (
    <motion.img
      src={src}
      alt={alt}
      aria-hidden={alt ? undefined : true}
      draggable={false}
      className={`select-none object-contain drop-shadow-[0_14px_20px_rgba(0,0,0,0.55)] ${className}`}
      animate={reducedMotion || still ? undefined : { y: [0, -10, 0], rotate: [-1.5, 1.5, -1.5] }}
      transition={reducedMotion || still ? undefined : { duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
    />
  );
}

/** Large readable card for TV text. */
export function TvCard({ children, className = '', accent, testId }: { children: ReactNode; className?: string; accent?: boolean; testId?: string }) {
  return (
    <section
      className={`rounded-[clamp(16px,1.6vw,32px)] border-2 bg-ink-2/90 px-[1.6vw] py-[1.2vw] text-[clamp(16px,1.45vw,30px)] leading-snug ${accent ? 'border-[var(--accent)]' : 'border-line'} ${className}`}
      data-testid={testId}
    >
      {children}
    </section>
  );
}

/**
 * Who the room is waiting on: every player's avatar, ticked when they're done (or have nothing to do this
 * step) and dimmed with an hourglass while the game still waits for them. Names only, never choices.
 */
export function TvWaiting({ players, waiting, away = [], title }: { players: string[]; waiting: string[]; away?: string[]; title?: string }) {
  const { member } = usePartyUi();
  const wait = new Set(waiting);
  const gone = new Set(away);
  const n = players.length;
  const big = useVw(5.2, 54, 104);
  const size = n <= 4 ? big : n <= 8 ? Math.round(big * 0.8) : n <= 12 ? Math.round(big * 0.65) : Math.round(big * 0.5);
  const done = players.filter((p) => !wait.has(p) && !gone.has(p)).length;
  return (
    <div className="w-full flex flex-col gap-[0.8vw]" data-testid="tv-waiting" aria-label={`${done} of ${n} ready`}>
      <p className="font-display font-black text-[clamp(16px,1.5vw,30px)] text-center">
        {title ?? (waiting.length ? `Waiting on ${waiting.length}` : 'Everyone is in!')}
      </p>
      <div className="flex flex-wrap justify-center gap-[0.9vw]">
        {players.map((id) => {
          const m = member(id);
          const w = wait.has(id);
          const a = gone.has(id);
          return (
            <motion.div key={id} animate={{ opacity: w || a ? 0.5 : 1, scale: w ? 0.94 : 1 }} className="flex flex-col items-center gap-1" style={{ width: size + 24 }} data-testid="tv-waiting-player" data-state={a ? 'away' : w ? 'waiting' : 'done'}>
              <span className="relative" style={{ width: size, height: size }}>
                {m ? <Avatar id={m.avatar} size={size} color={w || a ? '#3a2f55' : m.color} /> : null}
                <span className={`absolute -bottom-1 -right-1 grid place-items-center rounded-full ring-2 ring-ink ${w ? 'bg-ink-3 text-mute' : a ? 'bg-ink-3 text-mute' : 'bg-good text-ink'}`} style={{ width: Math.round(size * 0.42), height: Math.round(size * 0.42) }}>
                  {w ? <Hourglass size="60%" aria-label="still playing" /> : a ? <span className="text-[10px] font-black">AWAY</span> : <Check size="65%" strokeWidth={3.5} aria-label="done" />}
                </span>
              </span>
              <span className="font-bold truncate max-w-full text-[clamp(12px,1vw,20px)]">{m?.nickname ?? 'Former player'}</span>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

/** Small player tag with avatar for TV lists. */
export function TvPlayer({ id, scale = 1, className = '' }: { id: string | null | undefined; scale?: number; className?: string }) {
  const { member } = usePartyUi();
  const m = member(id);
  const size = Math.round(useVw(2.4, 28, 48) * scale);
  return (
    <span className={`inline-flex items-center gap-[0.5vw] min-w-0 ${className}`}>
      {m && <Avatar id={m.avatar} size={size} color={m.color} />}
      <span className="font-bold truncate">{m?.nickname ?? 'Former player'}</span>
    </span>
  );
}

/**
 * Scales phone-sized game components (fixed text-sm etc.) up for the TV with CSS zoom, so they lay out at
 * their larger size instead of being transformed: `scale` at 1920 wide, proportionally less on smaller
 * screens (never below 1). Wrap only phone components: vw-sized Tv* parts would be zoomed twice.
 */
export function TvZoom({ children, scale = 1.45, className = '' }: { children: ReactNode; scale?: number; className?: string }) {
  const w = useVw(100, 320, 7680);
  const z = Math.max(1, (scale * w) / 1920);
  return <div className={className} style={{ zoom: z }}>{children}</div>;
}
